import { useEffect, useRef, useState } from "react";
import { api, picUrl } from "@/lib/api";

/**
 * A text input that shows an @-mention autocomplete dropdown as the user types
 * `@partial`. Selecting a suggestion inserts `@username` into the input.
 * Behaves like a normal controlled input otherwise.
 */
export default function MentionInput({
  value,
  onChange,
  onEnterKey,
  placeholder,
  disabled,
  className,
  testId,
  autoFocus,
  multiline = false,
  rows = 3,
}) {
  const inputRef = useRef(null);
  const [caret, setCaret] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [mentionRange, setMentionRange] = useState(null); // { start, end, query }

  // Detect if the caret is inside an @-token (e.g. "hi @al|ex more") and
  // extract the token. Runs whenever value or caret changes.
  useEffect(() => {
    if (!value) {
      setMentionRange(null);
      setShowMenu(false);
      return;
    }
    // Look backwards from caret for '@' with no whitespace in between
    let start = caret - 1;
    while (start >= 0 && !/\s/.test(value[start]) && value[start] !== "@") {
      start -= 1;
    }
    if (start < 0 || value[start] !== "@") {
      setMentionRange(null);
      setShowMenu(false);
      return;
    }
    // Ensure @ is at start or after whitespace
    const prevChar = start === 0 ? " " : value[start - 1];
    if (!/\s/.test(prevChar) && start !== 0) {
      setMentionRange(null);
      setShowMenu(false);
      return;
    }
    const token = value.slice(start + 1, caret);
    if (!/^[a-zA-Z0-9_]*$/.test(token)) {
      setMentionRange(null);
      setShowMenu(false);
      return;
    }
    setMentionRange({ start, end: caret, query: token });
  }, [value, caret]);

  // Fetch suggestions when the query changes (>=1 char)
  useEffect(() => {
    if (!mentionRange) {
      setSuggestions([]);
      setShowMenu(false);
      return;
    }
    const q = mentionRange.query;
    if (!q) {
      // show recent suggestions? For simplicity: only fetch when at least 1 char
      setSuggestions([]);
      setShowMenu(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/users/search?q=${encodeURIComponent(q)}&limit=6`);
        if (cancelled) return;
        setSuggestions(r.data.users || []);
        setActiveIdx(0);
        setShowMenu((r.data.users || []).length > 0);
      } catch {
        if (!cancelled) {
          setSuggestions([]);
          setShowMenu(false);
        }
      }
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionRange?.query]);

  const commitSuggestion = (u) => {
    if (!mentionRange || !u?.username) return;
    const before = value.slice(0, mentionRange.start);
    const after = value.slice(mentionRange.end);
    // Insert `@username ` and keep the caret right after the space
    const insert = `@${u.username} `;
    const next = `${before}${insert}${after}`;
    onChange(next);
    const nextCaret = before.length + insert.length;
    // Restore caret on next tick
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(nextCaret, nextCaret);
        setCaret(nextCaret);
      }
    }, 0);
    setShowMenu(false);
    setSuggestions([]);
    setMentionRange(null);
  };

  const handleKeyDown = (e) => {
    if (showMenu && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        commitSuggestion(suggestions[activeIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowMenu(false);
        return;
      }
    }
    if (e.key === "Enter" && onEnterKey) {
      e.preventDefault();
      onEnterKey();
    }
  };

  const handleSelectOrClick = () => {
    if (inputRef.current) {
      setCaret(inputRef.current.selectionStart ?? 0);
    }
  };

  return (
    <div className="relative flex-1">
      {multiline ? (
        <textarea
          ref={inputRef}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          autoFocus={autoFocus}
          rows={rows}
          onChange={(e) => {
            onChange(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyDown={handleKeyDown}
          onSelect={handleSelectOrClick}
          onClick={handleSelectOrClick}
          onBlur={() => setTimeout(() => setShowMenu(false), 120)}
          className={className}
        />
      ) : (
        <input
          ref={inputRef}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          autoFocus={autoFocus}
          onChange={(e) => {
            onChange(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyDown={handleKeyDown}
          onSelect={handleSelectOrClick}
          onClick={handleSelectOrClick}
          onBlur={() => setTimeout(() => setShowMenu(false), 120)}
          className={className}
        />
      )}
      {showMenu && suggestions.length > 0 && (
        <ul
          data-testid="mention-menu"
          className="absolute bottom-full left-0 mb-2 w-full bg-[#0A0A0C] border border-[#00FFFF]/40 shadow-[0_0_18px_rgba(0,255,255,0.25)] z-50"
        >
          {suggestions.map((u, i) => (
            <li
              key={u.user_id}
              data-testid={`mention-suggestion-${u.username}`}
              onMouseDown={(e) => {
                // mouseDown fires before blur so the click actually registers
                e.preventDefault();
                commitSuggestion(u);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                i === activeIdx ? "bg-[#00FFFF]/10" : ""
              }`}
            >
              {u.picture ? (
                <img
                  src={picUrl(u.picture)}
                  alt=""
                  className="h-6 w-6 object-cover border border-white/10 flex-shrink-0"
                />
              ) : (
                <div className="h-6 w-6 bg-[#00FFFF]/20 flex items-center justify-center text-[#00FFFF] text-[10px] font-bold flex-shrink-0">
                  {(u.name || u.username || "?")[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white truncate">
                  <span className="font-semibold">@{u.username}</span>
                  {u.name && (
                    <span className="text-white/40 ml-2">{u.name}</span>
                  )}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Given a string of comment text, render it as React nodes with `@username`
 * segments rendered as inline cyan links and `#hashtag` segments rendered as
 * green links to the hashtag page.
 */
export function renderMentionText(text) {
  if (!text) return null;
  const parts = [];
  // Matches @user OR #tag, tokens 1-40 chars in [a-zA-Z0-9_]
  const re = /([@#])([a-zA-Z0-9_]{1,40})/g;
  let lastIndex = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const sigil = match[1];
    const token = match[2];
    if (sigil === "@") {
      parts.push(
        <a
          key={`m-${match.index}`}
          href={`/u/${token}`}
          className="text-[#00FFFF] hover:text-[#39FF14] font-semibold"
          onClick={(e) => e.stopPropagation()}
        >
          @{token}
        </a>
      );
    } else {
      parts.push(
        <a
          key={`h-${match.index}`}
          href={`/t/${token.toLowerCase()}`}
          className="text-[#39FF14] hover:text-[#00FFFF] font-semibold"
          onClick={(e) => e.stopPropagation()}
        >
          #{token}
        </a>
      );
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}
